import { useEffect } from "react";
import { useLang } from "@/contexts/LanguageContext";

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  robots?: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
  twitterCard?: string;
}

function cleanSchema(obj: any): any {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (typeof obj === "string") {
    return obj.trim() === "" ? undefined : obj;
  }

  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(item => cleanSchema(item))
      .filter(item => item !== undefined);
    return cleanedArray.length === 0 ? undefined : cleanedArray;
  }

  if (typeof obj === "object") {
    if (obj instanceof Date) {
      return obj;
    }

    const cleanedObj: Record<string, any> = {};
    let hasKeys = false;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const cleanedValue = cleanSchema(obj[key]);
        if (cleanedValue !== undefined) {
          cleanedObj[key] = cleanedValue;
          hasKeys = true;
        }
      }
    }
    return hasKeys ? cleanedObj : undefined;
  }

  return obj;
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  robots = "index, follow",
  schema,
  twitterCard = "summary_large_image"
}: SEOProps) {
  const { lang } = useLang();

  // Process and bundle schemas under a unified @graph container if multiple are passed
  let processedSchema: any = null;
  if (schema) {
    if (Array.isArray(schema)) {
      const cleanSchemas = schema.map(item => {
        if (item && typeof item === "object") {
          const { "@context": context, ...rest } = item;
          return rest;
        }
        return item;
      });
      processedSchema = {
        "@context": "https://schema.org",
        "@graph": cleanSchemas
      };
    } else {
      processedSchema = schema;
    }
    // Clean all null, undefined, empty string/array/object properties recursively
    processedSchema = cleanSchema(processedSchema);
  }

  const schemaString = processedSchema ? JSON.stringify(processedSchema) : "";

  useEffect(() => {
    // 1. Optimize Title to ~50-58 characters to prevent truncation on Google, X, and LinkedIn
    let fullTitle = title.trim();
    if (!fullTitle.includes("AutoNxt") && !fullTitle.includes("AutoNXT")) {
      fullTitle = `${fullTitle} | AutoNxt`;
    }
    if (fullTitle.length > 58) {
      const parts = fullTitle.split("|");
      if (parts.length > 1 && parts[0].trim().length <= 58) {
        fullTitle = parts[0].trim();
      } else {
        fullTitle = fullTitle.substring(0, 55).trim() + "...";
      }
    }
    document.title = fullTitle;

    // Helper functions for meta tags
    const setMetaProperty = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const setMetaName = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // 2. Set Site Name (Required by Discord & Social Cards)
    setMetaProperty("og:site_name", "AutoNxt Automation");

    // 3. Update OpenGraph and Twitter title
    setMetaProperty("og:title", fullTitle);
    setMetaName("twitter:title", fullTitle);

    // 4. Optimize Meta Description (130-150 chars for Google SERP) and OG Description (120-125 chars for mobile social previews)
    const defaultDesc = "AutoNxt is India's pioneer in electric & autonomous tractors for zero-emission farming and heavy commercial hauling. Book your demo today.";
    let baseDesc = (description && description.trim().length > 0) ? description.trim() : defaultDesc;

    // If description is under 120 chars, expand with brand positioning to fit SERP snippet
    if (baseDesc.length < 120) {
      const brandSuffix = " AutoNxt is India's pioneer in electric & autonomous tractors for zero-emission operations.";
      baseDesc = (baseDesc + brandSuffix).substring(0, 148).trim();
    }

    // A. Meta Description: 130 - 150 characters for SERP
    let metaDesc = baseDesc;
    if (metaDesc.length > 150) {
      const truncated = metaDesc.substring(0, 147);
      const lastSpace = truncated.lastIndexOf(" ");
      metaDesc = (lastSpace > 110 ? truncated.substring(0, lastSpace) : truncated) + "...";
    }

    // B. OpenGraph & Twitter Description: 120 - 125 characters max for mobile social previews
    let ogDesc = baseDesc;
    if (ogDesc.length > 125) {
      const truncated = ogDesc.substring(0, 122);
      const lastSpace = truncated.lastIndexOf(" ");
      ogDesc = (lastSpace > 90 ? truncated.substring(0, lastSpace) : truncated) + "...";
    }

    setMetaName("description", metaDesc);
    setMetaProperty("og:description", ogDesc);
    setMetaName("twitter:description", ogDesc);

    // 5. Update meta keywords (optional)
    if (keywords) {
      setMetaName("keywords", keywords);
    }

    // 5. Update Canonical Tag & OpenGraph URL
    const siteOrigin = "https://www.autonxt.in";
    let basePath = url
      ? (url.startsWith("http") ? new URL(url).pathname : url)
      : window.location.pathname;

    // Normalize trailing slash for AWS Amplify directory static hosting
    if (basePath !== "/" && !basePath.endsWith("/")) {
      basePath = `${basePath}/`;
    }

    // Canonical URL for the current active language
    const canonicalUrl = lang === "en"
      ? `${siteOrigin}${basePath}`
      : `${siteOrigin}${basePath}?lang=${lang}`;

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    setMetaProperty("og:url", canonicalUrl);

    // 5b. Update hreflang alternate links
    const alternatesList = [
      { hreflang: "en", url: `${siteOrigin}${basePath}` },
      { hreflang: "hi", url: `${siteOrigin}${basePath}?lang=hi` },
      { hreflang: "mr", url: `${siteOrigin}${basePath}?lang=mr` },
      { hreflang: "te", url: `${siteOrigin}${basePath}?lang=te` },
      { hreflang: "x-default", url: `${siteOrigin}${basePath}` }
    ];

    // Remove any existing alternate hreflang links to prevent duplicates
    const existingAlternates = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingAlternates.forEach(el => el.remove());

    // Insert new alternates
    alternatesList.forEach(item => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", item.hreflang);
      link.setAttribute("href", item.url);
      document.head.appendChild(link);
    });

    // 6. Update OpenGraph type and locale
    setMetaProperty("og:type", type);
    const ogLocale = lang === "hi" ? "hi_IN" : lang === "mr" ? "mr_IN" : lang === "te" ? "te_IN" : "en_IN";
    setMetaProperty("og:locale", ogLocale);

    // 7. Update OpenGraph & Twitter Image
    const defaultImage = `${siteOrigin}/opengraph.jpg`;
    const finalImage = image
      ? (image.startsWith("http") ? image : `${siteOrigin}${image.startsWith("/") ? image : `/${image}`}`)
      : defaultImage;

    setMetaProperty("og:image", finalImage);
    setMetaName("twitter:image", finalImage);

    // 8. Update Twitter Card type
    setMetaName("twitter:card", twitterCard);

    // 9. Update Robots Meta
    setMetaName("robots", robots);

    // 10. Update Schema.org Structured Data
    let schemaScript = document.getElementById("seo-schema-script") as HTMLScriptElement;
    if (schemaString) {
      if (!schemaScript) {
        schemaScript = document.createElement("script");
        schemaScript.id = "seo-schema-script";
        schemaScript.type = "application/ld+json";
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = schemaString;
    } else {
      if (schemaScript) {
        schemaScript.remove();
      }
    }
  }, [title, description, keywords, image, url, type, robots, schemaString, twitterCard, lang]);

  // Clean up schema and alternates on component unmount
  useEffect(() => {
    return () => {
      const schemaScript = document.getElementById("seo-schema-script");
      if (schemaScript) {
        schemaScript.remove();
      }
      const alternates = document.querySelectorAll('link[rel="alternate"][hreflang]');
      alternates.forEach(el => el.remove());
    };
  }, []);

  return null;
}
